import React, { useEffect, useState } from "react";
import ProductManagementListing from "./ProductManagementListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/ProductManagementSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import AddProductManagementWrapper from "../Add/AddProductManagementWrapper";
import EditProductManagementFormWrapper from "../Edit/EditProductManagementWrapper";
import {
  useChangeStatusProductManagementByIdMutation,
  useCreateSynkProductManagementByAPIMutation,
  useDeleteProductManagementByIdMutation,
  useGetAllProductManagementDataQuery,
} from "src/services/ProductManagementService";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import { usePermission } from "src/hooks/usePermission";
import { Tooltip } from "@mui/material";

type Props = {};

const ProductManagementListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isTableLoading,
  } = useSelector((state: RootState) => state.productManagement);
  const [selectedProductManagementId, setSelectedProductManagementId] =
    useState<string>();

  const [deleteProductManagement] = useDeleteProductManagementByIdMutation();
  const [changeStatusProductManagement] =
    useChangeStatusProductManagementByIdMutation();
  const { data, isLoading, isFetching } = useGetAllProductManagementDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "title",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });
  const [triggerSync, { isLoading: isBookLoading }] =
    useCreateSynkProductManagementByAPIMutation();
  const handleAdd = () => {
    triggerSync("").then((res: any) => {
      if (res?.data.status) {
        showToast("success", res?.data.message);
      } else {
        showToast("error", res?.data.message);
      }
    });
  };
  useEffect(() => {
    if (isLoading || isFetching || isBookLoading) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching, isBookLoading]);

  const handleDeleteProductManagement = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Product Management ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteProductManagement(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusProductManagement = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Advance Payment ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusProductManagement({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const moduleName = "PRODUCT";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const getFacultyNames = (row: any) =>
    row?.faculty
      ?.map((item: any) => item?.facultyName)
      ?.filter(Boolean)
      ?.join(", ") || "-";

  const columns: columnTypes[] = [
    {
      field: "productName",
      headerName: "Product Name",
      flex: "flex-[2.4_2.4_0%]",
      renderCell: (row: any) => {
        const imageUrl = row?.image?.[0];
        return (
          <div className="flex items-start gap-3 py-1 overflow-hidden w-full">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={row?.productName || "product"}
                className="w-12 h-12 object-cover rounded border border-slate-200 shrink-0 bg-slate-50"
              />
            ) : (
              <div className="w-12 h-12 rounded border border-slate-200 shrink-0 bg-slate-100" />
            )}
            <Tooltip title={row?.productName || "-"}>
              <div className="truncate font-normal">
                {row?.productName || "-"}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: "modeName",
      headerName: "Mode Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.modeName || "-"}>
          <div className="truncate capitalize">
            {row?.modeName || "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "varientCode",
      headerName: "Variant Code",
      flex: "flex-[1.2_1.2_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.varientCode || "-"}>
          <div className="truncate">{row?.varientCode || "-"}</div>
        </Tooltip>
      ),
    },
    {
      field: "productMrp",
      headerName: "MRP",
      flex: "flex-[0.7_0.7_0%]",
      renderCell: (row: any) => (
        <div className="font-normal">{row?.productMrp ?? "-"}</div>
      ),
    },
    {
      field: "productSellingPrice",
      headerName: "Selling Price",
      flex: "flex-[0.8_0.8_0%]",
      renderCell: (row: any) => (
        <div className="font-normal">{row?.productSellingPrice ?? "-"}</div>
      ),
    },
    {
      field: "courseName",
      headerName: "Course",
      flex: "flex-[0.9_0.9_0%]",
      renderCell: (row: any) => {
        const courseLabel = [row?.courseName, row?.levelName]
          .filter(Boolean)
          .join(" ");
        return (
          <Tooltip title={courseLabel || "-"}>
            <div className="truncate capitalize">
              {courseLabel || "-"}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "faculty",
      headerName: "Faculty",
      flex: "flex-[1.2_1.2_0%]",
      renderCell: (row: any) => {
        const facultyNames = getFacultyNames(row);
        return (
          <Tooltip title={facultyNames}>
            <div className="truncate capitalize">{facultyNames}</div>
          </Tooltip>
        );
      },
    },
   
  ];

  return (
    <>
      {" "}
      <div>
        <ProductManagementListing
          columns={columns}
          syncProduct={handleAdd}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddProductManagementWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditProductManagementFormWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedProductManagementId={selectedProductManagementId || ""}
        />
      ) : null}
    </>
  );
};

export default ProductManagementListingWrapper;
